import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AddToProductHeader } from './AddToProductHeader'
import { AddToProductTabBar } from './AddToProductTabBar'
import { AddToProductWorkspace } from './AddToProductWorkspace'
import { hasTexts, type AddToProductPayload } from './types'
import type { ProductSearchMatch } from '@/lib/api/products-search'

export interface AddToProductOverlayProps {
  payload: AddToProductPayload
  onClose: () => void
  onComplete?: () => void
}

const DIALOG_TITLE_ID = 'add-to-product-overlay-title'

export function AddToProductOverlay({
  payload,
  onClose,
  onComplete,
}: AddToProductOverlayProps) {
  const [selected, setSelected] = useState<ProductSearchMatch | null>(null)
  const textsPresent = hasTexts(payload)
  const [tab, setTab] = useState<'content' | 'media'>(
    textsPresent ? 'content' : 'media',
  )

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  useEffect(() => {
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = original
    }
  }, [])

  // Portal to <body> so the overlay is the last child of <body> and wins the
  // stacking contest against any other modal in the React tree above us (e.g.
  // a Radix Dialog hosting ImageDetailModal). z-[60] is a defensive bump above
  // the conventional z-50 modals.
  if (typeof document === 'undefined') return null
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={DIALOG_TITLE_ID}
      className="fixed inset-0 z-[60] flex flex-col bg-neutral-50 dark:bg-neutral-950"
    >
      <AddToProductHeader
        title={payload.title}
        onClose={onClose}
        titleId={DIALOG_TITLE_ID}
      />
      {selected && textsPresent && (
        <AddToProductTabBar tab={tab} onTabChange={setTab} />
      )}
      <AddToProductWorkspace
        payload={payload}
        tab={tab}
        selected={selected}
        onSelect={setSelected}
        onComplete={onComplete}
      />
    </div>,
    document.body,
  )
}
